import { usePerms } from 'contexts/PermsContext';
import useSirenAudio from 'features/Integrations/hooks/useSirenAudio';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useDeleteAudioMutation,
  useEditAudioMutation,
  useGetConnectedDevicesQuery,
  useSaveTempAudioMutation,
  useUpdateAudioMutation,
} from 'services/devices/devices.api';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import AssignByAudioSection from './components/AssignByAudioSection/AssignByAudioSection';
import AssociationConfirmationModal from './components/AssociationConfirmationModal/AssociationConfirmationModal';
import AudioManagerHeader from './components/AudioManagerHeader/AudioManagerHeader';
import DeleteAudioModal from './components/DeleteAudioModal/DeleteAudioModal';
import ManageDeviceAssociationsSection from './components/ManageDeviceAssociationsSection/ManageDeviceAssociationsSection';
import useCompactLayout from './hooks/useCompactLayout';
import useAudioAssociations from './hooks/useAudioAssociations';
import { getAudioNotificationSubject } from './utils/audioFiles.utils';
import {
  filterOptions,
  getAssociationUpdateResults,
  getAudioSaveErrorMessage,
  getDeviceOptions,
  getRelevantAudioDevices,
  getScopedDeviceOptions,
  groupOptionsBySystemAndType,
} from './utils/audioManager.utils';

/**
 * Manages siren audio files and their device associations.
 *
 * @param {object} props - Audio Manager configuration.
 * @returns {JSX.Element|null} The Audio Manager interface for the selected devices.
 */
const AudioManager = ({ deviceIds, group, onClose = () => {}, onPlayTemp = () => {} }) => {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const [saveTempAudio] = useSaveTempAudioMutation();
  const [updateAudio] = useUpdateAudioMutation();
  const [deleteAudio] = useDeleteAudioMutation();
  const [editAudio] = useEditAudioMutation();

  const perms = usePerms();
  const containerRef = useRef(null);
  const isCompactLayout = useCompactLayout(containerRef);

  // Keeps the same array reference unless a tracked device's id/type actually
  // changes, so unrelated device updates don't refetch every audio file.
  const deviceIdsKey = (deviceIds || []).join(',');
  const relevantDevices = useMemo(
    () => getRelevantAudioDevices(connectedDevices, deviceIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deviceIdsKey, connectedDevices],
  );
  const relevantDevicesKey = JSON.stringify(relevantDevices);
  const deviceObjectsRef = useRef({ key: null, value: [] });
  if (deviceObjectsRef.current.key !== relevantDevicesKey) {
    deviceObjectsRef.current = { key: relevantDevicesKey, value: relevantDevices };
  }
  const deviceObjects = deviceObjectsRef.current.value;
  const { allAudio } = useSirenAudio(deviceObjects);

  const [file, setFile] = useState(null);
  const [blob, setBlob] = useState(null);
  const [fileName, setFileName] = useState('');
  const [managerMode, setManagerMode] = useState('audio');
  const [deviceSearchValue, setDeviceSearchValue] = useState('');
  const [manageDeviceSearchValue, setManageDeviceSearchValue] = useState('');
  const [audioList, setAudioList] = useState([]);
  const [confirmationPlan, setConfirmationPlan] = useState(null);
  const [isSavingAssociations, setIsSavingAssociations] = useState(false);
  const savingAssociationsRef = useRef(false);
  const associationScopeKey = JSON.stringify([(deviceIds || []).map(String).sort(), !!group]);
  const associationScopeRef = useRef({ key: associationScopeKey });
  if (associationScopeRef.current.key !== associationScopeKey) {
    associationScopeRef.current = { key: associationScopeKey };
  }
  const isMountedRef = useRef(false);
  const [toDelete, setToDelete] = useState(null);
  const [toEdit, setToEdit] = useState(null);
  const [editName, setEditName] = useState('');
  const [updateAudioList, setUpdateAudioList] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setConfirmationPlan(null);
    setIsSavingAssociations(false);
    savingAssociationsRef.current = false;
  }, [associationScopeKey]);

  useEffect(() => {
    setAudioList(allAudio.filter((audio) => !!audio.device_ids));
  }, [allAudio]);

  const updateFile = (newFile) => {
    setBlob(null);
    setFile(newFile);
  };

  const updateBlob = (newBlob, newFileName) => {
    if (newBlob && file) setFile(null);
    setBlob(newBlob);
    setFileName(newFileName);
  };

  const deviceAssociationOptions = useMemo(
    () => getDeviceOptions(connectedDevices, group),
    [connectedDevices, group],
  );

  const manageDeviceOptions = useMemo(
    () => getScopedDeviceOptions(deviceAssociationOptions, deviceIds),
    [deviceAssociationOptions, deviceIds],
  );

  const filteredDeviceAssociationOptions = useMemo(
    () => filterOptions(deviceAssociationOptions, deviceSearchValue),
    [deviceAssociationOptions, deviceSearchValue],
  );

  const filteredManageDeviceOptions = useMemo(
    () => filterOptions(manageDeviceOptions, manageDeviceSearchValue),
    [manageDeviceOptions, manageDeviceSearchValue],
  );

  const {
    selectedAudios,
    selectedDevices,
    draftAudioList,
    updatePlan,
    onAudioSelectToggle,
    toggleAssociatedDevice,
    toggleManagedAudioForDevice,
    getEffectiveManagedAudioIdsForDevice,
    stageUpdates,
    applySuccessfulUpdates,
  } = useAudioAssociations({
    audioList,
    deviceIds,
    deviceOptions: deviceAssociationOptions,
    group,
    managerMode,
    scopeKey: associationScopeKey,
  });

  const canEditDelete = perms.testPermission([], ['Edit-DeleteSirenAudioFiles']);
  const hasSelectedAudio = selectedAudios.length > 0;
  const canEditDeviceAssociations = hasSelectedAudio && !group;

  const groupedDeviceAssociationOptions = useMemo(
    () =>
      groupOptionsBySystemAndType(
        filteredDeviceAssociationOptions,
        (items) => items.filter((device) => selectedDevices.includes(String(device.value))).length,
      ),
    [filteredDeviceAssociationOptions, selectedDevices],
  );

  const groupedManageDeviceOptions = useMemo(
    () =>
      groupOptionsBySystemAndType(
        filteredManageDeviceOptions,
        (items) =>
          items.filter((device) => getEffectiveManagedAudioIdsForDevice(device.value).length > 0)
            .length,
      ),
    [filteredManageDeviceOptions, draftAudioList],
  );

  const onPlayTempInt = () => onPlayTemp(fileName);

  const onEdit = (audio) => {
    setToEdit(audio.id);
    setEditName(audio.name);
  };

  const onResolveEdit = async () => {
    if (!toEdit || !editName) return;
    try {
      await editAudio({ audioId: toEdit, fileName: editName }).unwrap();
      setToEdit(null);
      setEditName('');
      notifySuccessToast(getAudioNotificationSubject(editName), {
        isAddOperation: false,
        isDeleteOperation: false,
      });
    } catch (error) {
      console.error('Error in AudioManager:onResolveEdit:', error);
      notifyErrorToast(getAudioNotificationSubject(editName), {
        isAddOperation: false,
        isDeleteOperation: false,
      });
    }
  };

  const onCancelEdit = () => {
    setEditName('');
    setToEdit(null);
  };

  const onConfirmDelete = async () => {
    if (!toDelete) return;
    const audioToDelete = audioList.find((audio) => audio.id === toDelete);
    try {
      await deleteAudio([toDelete]).unwrap();
      setAudioList((prev) => prev.filter((audio) => audio.id !== toDelete));
      notifySuccessToast(getAudioNotificationSubject(audioToDelete?.name), {
        isAddOperation: false,
        isDeleteOperation: true,
      });
    } catch (e) {
      console.error('Error in AudioManager:onConfirmDelete:', e);
      notifyErrorToast(getAudioNotificationSubject(audioToDelete?.name), {
        isAddOperation: false,
        isDeleteOperation: true,
      });
    } finally {
      setToDelete(null);
    }
  };

  const onSaveTemp = async (toDeleteTemp, override) => {
    try {
      await saveTempAudio({ toDelete: toDeleteTemp || false, override: override }).unwrap();
      setUpdateAudioList((prev) => !prev);
      notifySuccessToast(getAudioNotificationSubject(fileName), {
        isAddOperation: true,
      });
    } catch (e) {
      console.error('Error in AudioManager:onSaveTemp:', e);
      toast.error(getAudioSaveErrorMessage(e));
    }
  };

  const onConfirmAssociations = async () => {
    if (!confirmationPlan || savingAssociationsRef.current) return;
    savingAssociationsRef.current = true;
    setIsSavingAssociations(true);
    const plan = confirmationPlan;
    const scope = associationScopeRef.current;
    try {
      const results = await Promise.allSettled(
        plan.map(async ({ audioId, deviceIds }) => updateAudio({ audioId, deviceIds }).unwrap()),
      );
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          console.error(
            `Error in AudioManager:onConfirmAssociations (audio ${plan[index].audioId}):`,
            result.reason,
          );
        }
      });
      if (!isMountedRef.current || scope !== associationScopeRef.current) {
        // The modal closed or its devices changed: the toast is the only sign of a failure.
        if (results.some((result) => result.status === 'rejected')) {
          notifyErrorToast('Audio association', {
            isAddOperation: false,
            isDeleteOperation: false,
          });
        }
        return;
      }
      const { successfulUpdates, failedUpdates } = getAssociationUpdateResults(plan, results);
      applySuccessfulUpdates(successfulUpdates);

      if (failedUpdates.length) {
        setConfirmationPlan(failedUpdates);
        notifyErrorToast('Audio association', {
          isAddOperation: false,
          isDeleteOperation: false,
        });
        return;
      }

      notifySuccessToast('Audio association', {
        isAddOperation: false,
        isDeleteOperation: false,
      });
      setConfirmationPlan(null);
      onClose();
    } catch (e) {
      console.error('Error in AudioManager:onConfirmAssociations:', e);
      notifyErrorToast('Audio association', {
        isAddOperation: false,
        isDeleteOperation: false,
      });
    } finally {
      if (isMountedRef.current && scope === associationScopeRef.current) {
        savingAssociationsRef.current = false;
        setIsSavingAssociations(false);
      }
    }
  };

  const onDone = () => {
    if (!updatePlan.length) {
      onClose();
      return;
    }
    stageUpdates(updatePlan);
    setConfirmationPlan(updatePlan);
  };

  const sectionCardClass = `d-flex flex-column min-h-0 overflow-hidden rounded-2 border border-secondary border-opacity-50 bg-primary-shadow ${
    isCompactLayout ? 'flex-shrink-0' : 'flex-fill'
  }`;

  if (!deviceIds || deviceIds?.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className="d-flex flex-column gap-2 px-3 pb-3 pt-2 h-100 min-h-0 overflow-hidden"
    >
      <AudioManagerHeader managerMode={managerMode} onModeChange={setManagerMode} />

      <div className="d-flex flex-column flex-grow-1 min-h-0 overflow-y-auto overflow-x-hidden pe-1 customScrollbar-Y gap-3">
        <div
          hidden={managerMode !== 'audio'}
          className={
            managerMode === 'audio' ? 'd-flex flex-column flex-grow-1 min-h-0 gap-3' : 'd-none'
          }
        >
          <AssignByAudioSection
            sectionCardClass={sectionCardClass}
            isCompactLayout={isCompactLayout}
            file={file}
            audioList={audioList}
            blob={blob}
            updateFile={updateFile}
            updateBlob={updateBlob}
            onSaveTemp={onSaveTemp}
            onPlayTemp={onPlayTempInt}
            selectedAudios={selectedAudios}
            canEditDelete={canEditDelete}
            onAudioSelectToggle={onAudioSelectToggle}
            toEdit={toEdit}
            editName={editName}
            setEditName={setEditName}
            onEdit={onEdit}
            onResolveEdit={onResolveEdit}
            onCancelEdit={onCancelEdit}
            setToDelete={setToDelete}
            updateAudioList={updateAudioList}
            deviceSearchValue={deviceSearchValue}
            setDeviceSearchValue={setDeviceSearchValue}
            hasSelectedAudio={hasSelectedAudio}
            groupedDeviceAssociationOptions={groupedDeviceAssociationOptions}
            selectedDevices={selectedDevices}
            canEditDeviceAssociations={canEditDeviceAssociations}
            onToggleAssociatedDevice={toggleAssociatedDevice}
          />
        </div>
        <div
          hidden={managerMode !== 'device'}
          className={
            managerMode === 'device' ? 'd-flex flex-column flex-grow-1 min-h-0 gap-3' : 'd-none'
          }
        >
          <ManageDeviceAssociationsSection
            sectionCardClass={sectionCardClass}
            isCompactLayout={isCompactLayout}
            manageDeviceSearchValue={manageDeviceSearchValue}
            setManageDeviceSearchValue={setManageDeviceSearchValue}
            groupedManageDeviceOptions={groupedManageDeviceOptions}
            getEffectiveManagedAudioIdsForDevice={getEffectiveManagedAudioIdsForDevice}
            audioList={draftAudioList}
            toggleManagedAudioForDevice={toggleManagedAudioForDevice}
            toEdit={toEdit}
            editName={editName}
            setEditName={setEditName}
            onEdit={onEdit}
            onResolveEdit={onResolveEdit}
            onCancelEdit={onCancelEdit}
            setToDelete={setToDelete}
            canEditDelete={canEditDelete}
            updateAudioList={updateAudioList}
          />
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center flex-shrink-0 pt-1">
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onDone}
          disabled={
            (managerMode === 'audio' && selectedAudios.length < 1 && !updatePlan.length) ||
            (managerMode === 'device' && manageDeviceOptions.length < 1)
          }
        >
          Done
        </Button>
      </div>

      <DeleteAudioModal
        show={toDelete !== null}
        audioName={
          audioList.find((audio) => audio.id === toDelete)?.displayName ||
          audioList.find((audio) => audio.id === toDelete)?.name ||
          'This file'
        }
        onCancel={() => setToDelete(null)}
        onConfirm={onConfirmDelete}
      />
      <AssociationConfirmationModal
        show={confirmationPlan !== null}
        updates={confirmationPlan || []}
        isSaving={isSavingAssociations}
        onCancel={() => {
          if (!savingAssociationsRef.current) setConfirmationPlan(null);
        }}
        onConfirm={onConfirmAssociations}
      />
    </div>
  );
};

export default AudioManager;
